import React from "react";
import SearchForm from "@search/SearchForm";
import Searchbar from "@search/Searchbar";
import {
  renderFrom,
  renderMultiSelect,
  renderProfessions,
  renderSelect
} from "@search/filters";
import { useObserver } from "mobx-react";
import { CustomerContractsFilterManager } from "./CustomerContractsFilterManager";
import locale from "@bonliva-crm/locale";
import {
  CustomerContractAgreementType,
  CustomerContractCompany,
  CustomerContractStatus
} from "@bonliva-crm/shared";

type Props = {
  manager: CustomerContractsFilterManager;
};

export function CustomerContractsSearchForm(props: Props) {
  const { manager } = props;

  return useObserver(() => {
    // const ba = renderUsers(
    //   "Number",
    //   manager.filter.userIds,
    //   v => manager.updateFilter({ userIds: v }),
    //   manager.isFilterDirty("userIds")
    // );

    // `before` arg intentionally null: these are standalone "from <date>"
    // filters with no paired upper bound. Passing the field's own value as
    // `before` (the previous behavior) made the picker only allow earlier
    // dates than the current selection, so users couldn't raise a date.
    const contractStart = renderFrom(
      manager.filter.contractStart,
      (v) => manager.updateFilter({ contractStart: v }),
      manager.isFilterDirty("contractStart"),
      null,
      false,
      locale.StartDate
    );

    const contractEnd = renderFrom(
      manager.filter.contractEnd,
      (v) => manager.updateFilter({ contractEnd: v }),
      manager.isFilterDirty("contractEnd"),
      null,
      false,
      locale.EndDate
    );

    const currentContractEnd = renderFrom(
      manager.filter.currentContractEnd,
      (v) => manager.updateFilter({ currentContractEnd: v }),
      manager.isFilterDirty("currentContractEnd"),
      null,
      false,
      locale.customerContractEdit.currentEndDate
    );

    const contractProlongation = renderFrom(
      manager.filter.contractProlongation,
      (v) => manager.updateFilter({ contractProlongation: v }),
      manager.isFilterDirty("contractProlongation"),
      null,
      false,
      locale.customerContractEdit.prolongationDate
    );

    const contractPriceAdjustmentDate = renderFrom(
      manager.filter.contractPriceAdjustmentDate,
      (v) => manager.updateFilter({ contractPriceAdjustmentDate: v }),
      manager.isFilterDirty("contractPriceAdjustmentDate"),
      null,
      false,
      locale.customerContractEdit.adjustmentDate
    );

    const contractProfession = renderProfessions(
      manager.filter.professions,
      (v) => manager.updateFilter({ professions: v }),
      manager.isFilterDirty("professions"),
      false
    );

    const typeOfAgreement = renderSelect(
      locale.AgreementType,
      Object.values(CustomerContractAgreementType).toLabelValue(
        (v) => locale.customerContractAgreementType[v]
      ),
      false,
      manager.filter.typeOfAgreement,
      (v) => manager.updateFilter({ typeOfAgreement: CustomerContractAgreementType[v] }),
      false,
      manager.isFilterDirty("typeOfAgreement")
    );

    const company = renderSelect(
      locale.CompanyName,
      Object.values(CustomerContractCompany).toLabelValue(
        (v) => locale.customerContractCompany[v]
      ),
      false,
      manager.filter.company,
      (v) => manager.updateFilter({ company: CustomerContractCompany[v] }),
      false,
      manager.isFilterDirty("company")
    );

    const status = renderMultiSelect(
      locale.Status,
      Object.values(CustomerContractStatus).toLabelValue(
        (v) => locale.customerContractStatus[v]
      ),
      false,
      manager.filter.status,
      (v) => manager.updateFilter({ status: v as CustomerContractStatus[] }),
      false,
      manager.isFilterDirty("status")
    );

    const SearchBar = (
      <Searchbar
        onChange={(v) => manager.updateFilter({ text: v }, true)}
        value={manager.filter.text}
      />
    );

    return (
      <SearchForm searchbar={SearchBar}>
        {contractStart}
        {contractEnd} {currentContractEnd} {contractProlongation}
        {contractPriceAdjustmentDate} {contractProfession} {typeOfAgreement} {company} {status}
      </SearchForm>
    );
  });
}
